import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { useAiChatRouteSummary } from '../../contexts/AiChatRouteContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import CustomDataDisplay from '../../components/CustomDataDisplay'
import EntityComments from '../../components/EntityComments'
import EntityHistory from '../../components/EntityHistory'
import { Button, Input } from '../../components/ui'
import Icon from '../../components/Icon'
import { normalizeDate } from '../../utils/dateUtils'
import { formatRub } from '../../utils/formatRub'
import { DetailPageHeader, DetailSection, InfoGrid, KpiStrip, SummaryCard } from './detail-pages/DetailPageScaffold'
import './Company.css'

/** Год «С нами с»: из relationship_history (если есть год) или из created_at */
function getSinceYear(client) {
  const hist = client?.relationship_history?.trim()
  if (hist) {
    const yearMatch = hist.match(/\b(20\d{2})\b/)
    if (yearMatch) return yearMatch[1]
  }
  if (client?.created_at) {
    return new Date(client.created_at).getFullYear().toString()
  }
  return null
}

/** Реквизиты одним текстом: ИНН, ОГРН, при наличии address, city, website */
function formatRequisites(client) {
  const parts = []
  if (client?.inn) parts.push(`ИНН: ${client.inn}`)
  if (client?.ogrn) parts.push(`ОГРН: ${client.ogrn}`)
  if (client?.address || client?.city) {
    parts.push([client.address, client.city].filter(Boolean).join(', '))
  }
  if (client?.website) parts.push(client.website)
  return parts.length ? parts.join(' ') : '-'
}

function ClientCard() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { setHumanSummary } = useAiChatRouteSummary()
  const [client, setClient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [kpModal, setKpModal] = useState({ show: false, kp_link: '', kp_sent_at: '' })
  const [kpSaving, setKpSaving] = useState(false)
  const canUpdate = user?.permissions?.['clients.update']
  const canCreateContact = user?.permissions?.['contacts.create']
  const canUpdateContact = user?.permissions?.['contacts.update']
  const canCreateObject = user?.permissions?.['objects.create']
  const canCreateContract = user?.permissions?.['contracts.create']
  const hasObjects = client?.objects && client.objects.length > 0

  useEffect(() => {
    fetchClient()
  }, [id])

  useEffect(() => {
    if (!client || String(client.id) !== String(id)) {
      setHumanSummary(null)
      return undefined
    }
    const name = (client.name || '').trim()
    if (!name) {
      setHumanSummary(null)
      return undefined
    }
    setHumanSummary(`Клиент: ${name}`)
    return () => setHumanSummary(null)
  }, [client, id, setHumanSummary])

  const fetchClient = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/company/clients/${id}`)
      if (response.data && response.data.client) {
        setClient(response.data.client)
      }
    } catch (error) {
      console.error('Error fetching client:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('ru-RU')
  }


  const openKpModal = () => {
    const today = new Date().toISOString().slice(0, 10)
    setKpModal({
      show: true,
      kp_link: client?.kp_link || '',
      kp_sent_at: normalizeDate(client?.kp_sent_at) || today
    })
  }

  usePageHeader({ title: null, hideTopHeader: true }, [])

  const saveKp = async () => {
    setKpSaving(true)
    try {
      const payload = {
        name: client.name,
        inn: client.inn,
        ogrn: client.ogrn,
        type: client.type,
        comments: client.comments,
        address: client.address,
        city: client.city,
        relationship_history: client.relationship_history,
        kp_link: kpModal.kp_link || null,
        kp_sent_at: kpModal.kp_sent_at || null,
        website: client.website,
        status: client.status,
        custom_data: client.custom_data || {}
      }
      await api.put(`/company/clients/${id}`, payload)
      await fetchClient()
      setKpModal(prev => ({ ...prev, show: false }))
    } catch (e) {
      console.error('Save KP error:', e)
    } finally {
      setKpSaving(false)
    }
  }

  if (loading) {
    return <div className="loading">Загрузка...</div>
  }

  if (!client) {
    return <div className="no-data">Клиент не найден</div>
  }

  const sinceYear = getSinceYear(client)
  const requisitesText = formatRequisites(client)
  const menuIcon = (name) => <Icon name={name} size={14} />
  const contracts = client.contracts || []
  const objects = client.objects || []
  const contacts = client.contacts || []
  const totalContractsAmount = client.contracts_total_amount ?? contracts.reduce((sum, contract) => sum + Number(contract.total_amount || 0), 0)
  const totalActsAmount = client.acts_amount ?? contracts.reduce((sum, contract) => sum + Number(contract.acts_amount || 0), 0)
  const clientRemainder = Math.max(0, Number(totalContractsAmount || 0) - Number(totalActsAmount || 0))

  return (
    <div className="company-page entity-detail-page entity-detail-redesign client-detail-redesign">
      <DetailPageHeader
        breadcrumbs={[
          { label: 'Клиенты', to: '/company/clients' },
          { label: client.name || 'Клиент' }
        ]}
        title={client.name || 'Клиент'}
        status={client.status || undefined}
        subtitle={`${client.type || 'Клиент'}${sinceYear ? ` — с нами с ${sinceYear} г.` : ''}`}
        onBack={() => navigate('/company/clients')}
        actions={[
          canUpdate ? {
            id: 'client-kp',
            label: 'Отправлено КП',
            icon: menuIcon('file'),
            onSelect: openKpModal,
          } : null,
          canUpdate ? {
            id: 'edit-client',
            label: 'Редактировать клиента',
            icon: menuIcon('edit'),
            onSelect: () => navigate(`/company/clients/${id}/edit`),
          } : null,
        ]}
      />

      <DetailSection title="Паспорт клиента">
        <InfoGrid
          items={[
            { label: 'Название', value: client.name || '-' },
            { label: 'Тип', value: client.type || '-' },
            { label: 'Реквизиты', value: requisitesText, full: true, muted: requisitesText === '-' },
            client.kp_link ? {
              label: 'Ссылка на КП',
              value: <a href={/^https?:\/\//i.test(client.kp_link) ? client.kp_link : `https://${client.kp_link}`} target="_blank" rel="noopener noreferrer">{client.kp_link}</a>,
            } : null,
            client.kp_sent_at ? { label: 'Дата отправки КП', value: formatDate(client.kp_sent_at) } : null,
            { label: 'Комментарии', value: client.comments || 'Отсутствуют в базе', full: true, muted: !client.comments },
          ]}
        />
      </DetailSection>

      <KpiStrip
        items={[
          { label: 'Объектов', value: String(objects.length), tone: 'info' },
          { label: 'Сумма по договорам', value: formatRub(totalContractsAmount), tone: 'accent' },
          { label: 'Заактировано', value: formatRub(totalActsAmount), tone: 'success' },
          { label: 'Остаток', value: formatRub(clientRemainder), tone: 'warning' },
        ]}
      />

      {client.custom_data && Object.keys(client.custom_data).length > 0 && (
        <DetailSection title="Дополнительные поля">
          <CustomDataDisplay entityType="clients" customData={client.custom_data} />
        </DetailSection>
      )}

      <div className="client-two-col">
        <DetailSection
          title="Объекты"
          actions={
            canCreateObject ? (
              <Button onClick={() => navigate('/company/objects/new', { state: { clientId: id } })} variant="primary" className="btn-add">
                <Icon name="plus" size={12} /> Добавить
              </Button>
            ) : null
          }
        >
          {objects.length > 0 ? (
            <div className="list-cards">
              {objects.map((object) => {
                const area = object.living_area != null && object.living_area !== ''
                  ? `${object.living_area} м²`
                  : null
                const parking = object.parking_spaces != null && object.parking_spaces !== ''
                  ? `${object.parking_spaces} машиномест`
                  : null
                return (
                  <SummaryCard
                    key={object.id}
                    title={object.name || 'Объект'}
                    status={object.type || undefined}
                    meta={object.address || 'Адрес не указан'}
                    rightMeta={[area, parking].filter(Boolean).join(' · ')}
                    to={`/object/${object.id}`}
                  />
                )
              })}
            </div>
          ) : (
            <p className="info-note">Объекты не найдены</p>
          )}
        </DetailSection>

        <DetailSection
          title="Контакты"
          actions={
            canCreateContact ? (
              <Button onClick={() => navigate(`/company/clients/${id}/contacts/new`)} variant="primary" className="btn-add">
                <Icon name="plus" size={12} /> Добавить
              </Button>
            ) : null
          }
        >
          {contacts.length > 0 ? (
            <div className="list-cards">
              {contacts.map((contact) => (
                <SummaryCard
                  key={contact.id}
                  title={contact.full_name || 'Контакт'}
                  status={contact.role || undefined}
                  meta={[contact.email, contact.phone].filter(Boolean).join(' · ') || 'Контакты не указаны'}
                  onClick={canUpdateContact ? () => navigate(`/company/clients/${id}/contacts/${contact.id}/edit`) : undefined}
                />
              ))}
            </div>
          ) : (
            <p className="info-note">Контакты не указаны</p>
          )}
        </DetailSection>
      </div>

      <DetailSection
        title="История договоров"
        className="client-contract-history"
        actions={
          canCreateContract && hasObjects ? (
            <Button
              onClick={() => navigate('/company/contracts/new', {
                state: { clientId: id, objectId: objects[0]?.id }
              })}
              variant="primary"
              className="btn-add"
            >
              <Icon name="plus" size={12} /> Добавить
            </Button>
          ) : null
        }
      >
        {contracts.length > 0 ? (
          <div className="list-cards">
            {contracts.map((contract, contractIdx) => {
              const amount = Number(contract.total_amount || 0)
              const acted = Number(contract.acts_amount || 0)
              const progress = amount > 0 ? Math.round((acted / amount) * 100) : 0
              return (
                <SummaryCard
                  key={`${contract.id || 'contract'}-${contractIdx}`}
                  title={contract.number || 'Договор'}
                  status={contract.status || undefined}
                  meta={`до ${formatDate(contract.contract_date)}`}
                  amount={formatRub(contract.total_amount)}
                  sub={`заактировано ${formatRub(contract.acts_amount ?? 0)}`}
                  progress={progress}
                  progressTone={progress >= 90 ? 'green' : progress > 0 ? 'orange' : 'grey'}
                  to={`/contract/${contract.id}`}
                />
              )
            })}
          </div>
        ) : (
          <p className="info-note">Договоры не найдены</p>
        )}
      </DetailSection>

      <EntityComments entityType="clients" entityId={id} />

      {user?.role === 'admin' && <EntityHistory entityType="clients" entityId={id} />}
      {kpModal.show && (
        <div className="modal-overlay" onClick={() => setKpModal(prev => ({ ...prev, show: false }))}>
          <div className="modal-content" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="kp-modal-title">
            <h3 id="kp-modal-title">Отправлено КП</h3>
            <div className="filter-group" style={{ marginBottom: 'var(--spacing-md)' }}>
              <label htmlFor="kp-link">Ссылка на КП</label>
              <Input
                id="kp-link"
                type="url"
                value={kpModal.kp_link}
                onChange={e => setKpModal(prev => ({ ...prev, kp_link: e.target.value }))}
                placeholder="https://..."
              />
            </div>
            <div className="filter-group" style={{ marginBottom: 'var(--spacing-md)' }}>
              <label htmlFor="kp-sent-at">Дата отправки</label>
              <Input
                id="kp-sent-at"
                type="date"
                value={kpModal.kp_sent_at}
                onChange={e => setKpModal(prev => ({ ...prev, kp_sent_at: e.target.value }))}
              />
            </div>
            <div className="modal-actions">
              <Button variant="ghost" onClick={() => setKpModal(prev => ({ ...prev, show: false }))}>
                Отмена
              </Button>
              <Button variant="primary" onClick={saveKp} disabled={kpSaving}>
                {kpSaving ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientCard
